import { SURPRISE_BAG_CARD_SOURCE } from '@pkg/config/sanity/portableEditor';
import type { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import type { SurpriseBag } from '@pkg/types/surpriseBag';
import { getLanguageCodeFromLocale } from '@pkg/utilities/i18n';

export interface SurpriseBagCardLocation {
  label?: string;
  latitude?: number;
  longitude?: number;
}

export interface SurpriseBagCardValue {
  source?: string;
  itemId?: string;
  location?: SurpriseBagCardLocation;
  showSoldOut?: boolean;
  itemSubCategories?: string[];
}

export interface SurpriseBagCardRequest {
  key: string;
  url: string;
  query?: Record<string, string>;
  extract: (response: unknown) => SurpriseBag | null;
}

const ITEM_ID_PATTERN = /^\d+$/;

export function isRenderableBag(bag: SurpriseBag | null | undefined): bag is SurpriseBag {
  return Boolean(
    bag?.item?.itemId && bag.item.itemPrice && (bag.store?.storeName || bag.displayName),
  );
}

function resolveById(itemId: string | undefined, locale: string): SurpriseBagCardRequest | null {
  const id = itemId?.trim();
  if (!id || !ITEM_ID_PATTERN.test(id)) {
    return null;
  }

  return {
    key: `surprise-bag-card:id:${id}:${locale}`,
    url: `/api/surprise-bags/${id}`,
    query: { locale },
    extract: (response) => (response as { payload?: SurpriseBag } | null)?.payload ?? null,
  };
}

function normalizeSubCategories(itemSubCategories: string[] | undefined): string[] {
  if (!Array.isArray(itemSubCategories)) {
    return [];
  }

  return [...new Set(
    itemSubCategories
      .filter((id): id is string => typeof id === 'string')
      .map(id => id.trim())
      .filter(Boolean),
  )].sort();
}

function resolveByLocation(
  value: SurpriseBagCardValue,
  locale: string,
): SurpriseBagCardRequest | null {
  const { latitude, longitude } = value.location ?? {};
  if (typeof latitude !== 'number' || typeof longitude !== 'number') {
    return null;
  }

  const withStockOnly = value.showSoldOut ? '0' : '1';
  const subCategories = normalizeSubCategories(value.itemSubCategories);

  return {
    key: [
      'surprise-bag-card:location',
      latitude,
      longitude,
      locale,
      withStockOnly,
      subCategories.join('_'),
    ].join(':'),
    url: '/api/surprise-bags',
    query: {
      lat: String(latitude),
      lng: String(longitude),
      locale,
      language: getLanguageCodeFromLocale(locale as LOCALE_CODE),
      page: '0',
      size: '1',
      withStockOnly,
      ...(subCategories.length ? { itemSubCategories: subCategories.join(',') } : {}),
    },
    extract: (response) =>
      (response as { payload?: { items?: SurpriseBag[] } } | null)?.payload?.items?.[0] ?? null,
  };
}

export function resolveSurpriseBagCardRequest(
  value: SurpriseBagCardValue | undefined,
  locale: string,
): SurpriseBagCardRequest | null {
  switch (value?.source ?? SURPRISE_BAG_CARD_SOURCE.ID) {
    case SURPRISE_BAG_CARD_SOURCE.ID:
      return resolveById(value?.itemId, locale);
    case SURPRISE_BAG_CARD_SOURCE.LOCATION:
      return value ? resolveByLocation(value, locale) : null;
    default:
      return null;
  }
}
